import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { IonButton } from '@ionic/angular/standalone';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Flexibility, Question } from 'src/app/services/slip.service';
import { Status } from '../ssqw-form-slide/status';

export interface SummaryItem {
  slide: number;
  question: Question;
  status: Status;
}

export interface SummaryItemViewModel {
  slideNumber: number;
  renderAs: 'text' | 'list';
  question: string;
  textValue?: string;
  listValue?: { items: { legend: string }[] };
  flexibility: Flexibility;
  errors: string[];
}

@Component({
  selector: 'app-ssqw-summary-slide',
  templateUrl: './ssqw-summary-slide.component.html',
  styleUrls: ['./ssqw-summary-slide.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, AsyncPipe, IonButton]
})
export class SsqwSummarySlideComponent implements OnInit {
  @Input() items$: Observable<SummaryItem[]>;
  @Output() switchSlide = new EventEmitter<number>();

  summaryItems$: Observable<SummaryItemViewModel[]>;

  ngOnInit(): void {
    this.summaryItems$ = this.items$.pipe(
      map(items =>
        items.map((i) => {
          switch (i.question.answerType) {
            case 'date':
              return {
                slideNumber: i.slide,
                renderAs: 'text',
                question: i.question.questionWording,
                textValue: i.status.value ? moment(i.status.value).format('DD MMM yy') : '-',
                flexibility: i.question.flexibility,
                errors: this.formatErrors(i.status.errors)
              };
            case 'short-text':
            case 'long-text':
            case 'email':
            case 'phone':
            case 'number':
              return {
                slideNumber: i.slide,
                renderAs: 'text',
                question: i.question.questionWording,
                textValue: i.status.value ?? '-',
                flexibility: i.question.flexibility,
                errors: this.formatErrors(i.status.errors)
              };
            case 'yes-no':
              return {
                slideNumber: i.slide,
                renderAs: i.status.value ? 'list' : 'text',
                question: i.question.questionWording,
                textValue: '-',
                listValue: i.status.value
                  ? {
                      items: [{ legend: i.status.value.label }]
                    }
                  : { items: [] },
                flexibility: i.question.flexibility,
                errors: this.formatErrors(i.status.errors)
              };
            case 'multiple-choice':
              if (i.question.options.isMultiSelect) {
                return {
                  slideNumber: i.slide,
                  renderAs: i.status.value ? 'list' : 'text',
                  question: i.question.questionWording,
                  textValue: '-',
                  listValue: i.status.value
                    ? {
                        items: i.status.value.checked.map((c) => {
                          const label = c.label === 'Other' ? `${c.label} - ${i.status.value.otherContent}` : c.label;
                          return {
                            legend: label
                          };
                        })
                      }
                    : { items: [] },
                  flexibility: i.question.flexibility,
                  errors: this.formatErrors(i.status.errors)
                };
              } else {
                return {
                  slideNumber: i.slide,
                  renderAs: i.status.value ? 'list' : 'text',
                  question: i.question.questionWording,
                  textValue: '-',
                  listValue: i.status.value
                    ? {
                        items: [
                          {
                            legend:
                              i.status.value.label === 'Other'
                                ? `${i.status.value.label} - ${i.status.value.otherContent}`
                                : i.status.value.label
                          }
                        ]
                      }
                    : { items: [] },
                  flexibility: i.question.flexibility,
                  errors: this.formatErrors(i.status.errors)
                };
              }
            default:
              return null;
          }
        })
      )
    );
  }

  private formatErrors(errors: ValidationErrors): string[] {
    const results: string[] = [];
    if (errors?.required) results.push('A value is required');
    if (errors?.email) results.push('A valid email is required');
    if (errors?.select) results.push('A selection is required');
    if (errors?.numeric) results.push('Please enter a valid number');
    return results;
  }
}
